import * as THREE from "three";
import { POLYGONS, VARS  } from "../Global/variables";
import { deselectPanels, selectSinglePanel } from "../Panels/PanelFunctions";
import { selectSegment } from "../Segments/Functions";
import { deselectObstacle, selectObstacle } from "../Obstacles/ObstacleFunctions";
import { createSegmentPlane } from "../Global/functions";

// Main selection function triggered by a mouse click or similar interaction
export function selection(
  selectedIndices,
  setSelectedIndices,
  setPolygonDetails,
  mouse,
  sceneManager,
  setShowPanelSettings,
  setSolarPanel,
  polygonState
) {
  const raycaster = new THREE.Raycaster();
  raycaster.setFromCamera(mouse, sceneManager.camera);

  // 🔒 1) If a vertex drag is ongoing, don’t change selection at all
  if (VARS.isVertexDragActive) {
    return;
  }

  // 🔍 2) Check if the click hit a vertex marker first
  //      (they are created with name = "vertex" in createVertex)
  const fullIntersects = raycaster.intersectObjects(
    sceneManager.scene.children,
    true
  );

  if (fullIntersects.length > 0 && fullIntersects[0].object.name === "vertex") {
    // Click is on a vertex → do NOT run any of the segment/panel/obstacle selection logic
    // DragControls will handle moving that vertex.
    return;
  }

  // 🧠 3) Existing behavior stays the same after that
  if (selectedIndices.segmentIndex === null) {
    // No segment selected → only segment selection
    segmentSelection(
      selectedIndices,
      setSelectedIndices,
      setPolygonDetails,
      raycaster,
      setSolarPanel,
      setShowPanelSettings
    );
  } else {
    // Segment already selected → panels / obstacles / segments
    panelSelection(
      selectedIndices,
      setSelectedIndices,
      setShowPanelSettings,
      raycaster,
      setSolarPanel,
      polygonState
    );
    obstacleSelection(selectedIndices, setSelectedIndices, raycaster);
    segmentSelection(
      selectedIndices,
      setSelectedIndices,
      setPolygonDetails,
      raycaster,
      setSolarPanel,
      setShowPanelSettings
    );
  }
}

// Checks for selection of a segment (polygon shape)
function segmentSelection(
  selectedIndices,
  setSelectedIndices,
  setPolygonDetails,
  raycaster,
  setSolarPanel,
  setShowPanelSettings
) {
  for (let i = 0; i < POLYGONS.length; i++) {
    const intersect = raycaster.intersectObject(POLYGONS[i].shape, false); // Check intersection with polygon shape
    if (intersect.length > 0 && i !== selectedIndices.segmentIndex) {
      setSolarPanel({ id: null }); // Clear solar panel data
      setShowPanelSettings(false); // Hide panel settings UI
      selectSegment(selectedIndices, i, setSelectedIndices, setPolygonDetails); // Select new segment
      break;
    }
  }
}

// Checks for selection of a panel within a segment
function panelSelection(
  selectedIndices,
  setSelectedIndices,
  setShowPanelSettings,
  raycaster,
  setSolarPanel,
  polygonsState
) {
  const panels = POLYGONS[selectedIndices.segmentIndex].panels.map((pan) => pan.panel); // Get panel meshes
  let intersect = raycaster.intersectObjects(panels, false); // Check intersection with panels
  if (intersect.length > 0) {
    const panelIndexToAdd = panels.indexOf(intersect[0].object); // Get index of intersected panel
    if (selectedIndices.panelIndex?.includes(panelIndexToAdd)) return; // If already selected, skip

    // Set the current solar panel data from state
    setSolarPanel({ ...polygonsState[selectedIndices.segmentIndex].panels[panelIndexToAdd] });
    selectSinglePanel(panelIndexToAdd, setSelectedIndices, selectedIndices, setShowPanelSettings); // Select the panel
  } else {
    // If no panel intersected, deselect
    setSolarPanel({ id: null });
    setShowPanelSettings(false);
    deselectPanels(selectedIndices.panelIndex, setSelectedIndices, selectedIndices);
  }
}

// Checks for selection of an obstacle (e.g., obstruction line) within a segment
function obstacleSelection(selectedIndices, setSelectedIndices, raycaster) {
  const { segmentIndex, obstacleIndex, panelIndex } = selectedIndices;
  if (segmentIndex === null || segmentIndex === undefined) return;

  const obstacles = POLYGONS[segmentIndex].obstructions; // Get obstacle meshes

  for (let i = 0; i < obstacles.length; i++) {
    raycaster.params.Line = { threshold: 0.05 }; // Set raycasting precision for lines
    const intersect = raycaster.intersectObject(obstacles[i].mesh, false); // Check intersection with obstacle
    if (intersect.length > 0) {
      const plane = createSegmentPlane(segmentIndex);
      const segIntersect = new THREE.Vector3();
      raycaster.ray.intersectPlane(plane, segIntersect); // Also intersect with shape
      if (i !== obstacleIndex) {
        // ✅ Pass explicit segmentIndex into selectObstacle
        selectObstacle(segmentIndex, selectedIndices, i, setSelectedIndices); // Select new obstacle
        obstacles[i].mesh.userData.originalPosition = segIntersect; // Store original intersection point
        return;
      } else return; // Already selected obstacle, do nothing
    }
  }

  // If no obstacle intersected and one was previously selected, deselect it
  if (obstacleIndex !== null) {
    deselectObstacle(obstacles[obstacleIndex]);
    setSelectedIndices({ segmentIndex: segmentIndex, obstacleIndex: null, panelIndex: panelIndex }); // Clear obstacle index
  }
}

// Helper function to return the index of a hovered panel (used for orientation or placement)
export function panelOrientation(selectedIndices, mouse, sceneManager) {
  const raycaster = new THREE.Raycaster();
  raycaster.setFromCamera(mouse, sceneManager.camera);
  let panelIndex = null;

  if (selectedIndices.segmentIndex !== null) {
    const panels = POLYGONS[selectedIndices.segmentIndex].panels.map((pan) => pan.panel); // Get panel meshes
    let intersect = raycaster.intersectObjects(panels, false); // Check intersection
    if (intersect.length > 0) {
      panelIndex = panels.indexOf(intersect[0].object); // Get index of the intersected panel
    }
  }

  return panelIndex; // Return the index or null
}
